<script lang="ts">
  import DashboardLayout from '../DashboardLayout.svelte';
  import DashHero from '../shared/DashHero.svelte';
  import BedMapWidget from '../widgets/BedMapWidget.svelte';
  import BedSemaphoreGrid from '../widgets/BedSemaphoreGrid.svelte';
  import ActiveIngresosQueue from '../widgets/ActiveIngresosQueue.svelte';
  import CitasHoyQueue from '../widgets/CitasHoyQueue.svelte';
  import OpenCuentasWidget from '../widgets/OpenCuentasWidget.svelte';
  import LabQueueWidget from '../widgets/LabQueueWidget.svelte';
  import KardexQueueWidget from '../widgets/KardexQueueWidget.svelte';
  import ImagenQueueWidget from '../widgets/ImagenQueueWidget.svelte';
  import QuirofanoAgendaWidget from '../widgets/QuirofanoAgendaWidget.svelte';
  import OpsTimeline from '../widgets/OpsTimeline.svelte';
  import QuickActions from '../widgets/QuickActions.svelte';
  import AlertBanner, { type AlertItem } from '../widgets/AlertBanner.svelte';
  import AltasPendientesWidget from '../widgets/AltasPendientesWidget.svelte';
  import KpiCard from '../widgets/KpiCard.svelte';
  import { Dashboard, Cuentas, Laboratorio, Imagenologia, type DashboardResumen, type Cuenta } from '../../../lib/api';
  import { seedClinicByOrg } from '../../../lib/clinic-demo-seed';
  import { orgFromDemoTenant, isDemoTenant } from '../../../lib/clinic-preview.svelte';
  import { isPilotMode } from '../../../lib/pilot';
  import { session } from '../../../lib/auth.svelte';
  import { tenantCtx } from '../../../lib/tenant.svelte';

  let { onNavigate }: { onNavigate: (tab: string) => void } = $props();

  let resumen = $state<DashboardResumen | null>(null);
  let semaforo = $state<DashboardResumen['semaforo'] | null>(null);
  let cuentasAbiertas = $state(0);
  let facturadoMes = $state(0);
  let labPendientes = $state(0);
  let imgPendientes = $state(0);
  let alerts = $state<AlertItem[]>([]);
  let error = $state<string | null>(null);

  function fmtQ(n: number) {
    return new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ', maximumFractionDigits: 0 }).format(n);
  }

  async function cargar() {
    error = null;
    try {
      const [r, cuentas, solLab, procLab, solImg] = await Promise.all([
        Dashboard.resumen(),
        Cuentas.list().catch(() => [] as Cuenta[]),
        Laboratorio.ordenes('SOLICITADA').catch(() => []),
        Laboratorio.ordenes('EN_PROCESO').catch(() => []),
        Imagenologia.ordenes('SOLICITADA').catch(() => []),
      ]);
      resumen = r;
      semaforo = r.semaforo;
      labPendientes = solLab.length + procLab.length;
      imgPendientes = solImg.length;

      const abiertas = cuentas.filter(c => c.estado === 'ABIERTA' || !c.estado);
      cuentasAbiertas = abiertas.length;
      facturadoMes = cuentas.reduce((s, c) => s + (c.total ?? 0), 0);

      const mant = (r.semaforo?.porColor?.gris ?? 0) + (r.camasMantenimiento ?? 0);
      const nuevasAlertas: AlertItem[] = [];
      if (mant > 0) {
        nuevasAlertas.push({
          level: 'warn',
          title: `${mant} camas en mantenimiento o bloqueadas`,
          detail: 'Revise el semáforo antes de nuevos ingresos.',
        });
      }
      if (r.urgenciasActivas && r.urgenciasActivas > 0) {
        nuevasAlertas.push({
          level: 'critical',
          title: `${r.urgenciasActivas} pacientes en urgencias`,
          detail: 'Capacidad crítica en ER/URG.',
        });
      }
      if (labPendientes > 10) {
        nuevasAlertas.push({
          level: 'warn',
          title: `${labPendientes} órdenes lab pendientes`,
          detail: 'Backlog de validación LIS.',
        });
      }
      if (cuentasAbiertas > 15) {
        nuevasAlertas.push({
          level: 'info',
          title: `${cuentasAbiertas} cuentas abiertas`,
          detail: 'Revise facturación y cierres pendientes.',
        });
      }
      alerts = nuevasAlertas;
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  let seedMsg = $state<string | null>(null);
  const esDemo = $derived(isPilotMode() || isDemoTenant(session.value?.tenantId));

  async function seedDemo() {
    error = null;
    seedMsg = null;
    try {
      const tid = session.value?.tenantId ?? 'clinica-demo-medica';
      const org = orgFromDemoTenant(tid) ?? 'clinica-medica';
      const r = await seedClinicByOrg(org);
      seedMsg = r.ok
        ? `${r.message}${r.details?.length ? ' · ' + r.details.slice(0, 3).join(' · ') : ''}`
        : r.message;
      await cargar();
    } catch (e: any) {
      error = e?.message ?? String(e);
    }
  }

  const quickActions = [
    { id: 'semaforo', label: 'Semáforo', icon: '' },
    { id: 'ingresos', label: 'Ingresos', icon: '' },
    { id: 'cuentas', label: 'Cuentas', icon: '' },
    { id: 'configuracion', label: 'Marca y firmas', icon: '' },
    { id: 'farmaciaPos', label: 'Recetas POS', icon: '' },
    { id: 'usuarios', label: 'Usuarios', icon: '' },
    { id: 'reportes', label: 'Reportes MSPAS', icon: '' },
    { id: 'laboratorio', label: 'Laboratorio', icon: '' },
    { id: 'quirofano', label: 'Quirófano', icon: '' },
    { id: 'altas', label: 'Altas', icon: '' },
  ];

  $effect(() => { cargar(); const t = setInterval(cargar, 30000); return () => clearInterval(t); });
</script>

<DashboardLayout title="Centro de comando" subtitle="Vista operativa del hospital" variant="admin">
  {#snippet actions()}
    <button type="button" class="glass-btn" onclick={cargar}>Recargar</button>
    {#if esDemo}<button type="button" class="glass-btn seed-btn" onclick={seedDemo}>Sembrar datos demo</button>{/if}
  {/snippet}

  <DashHero
    emoji=''
    title="Centro de comando"
    subtitle="Ocupación, ingresos, colas clínicas y facturación"
    variant="admin"
  />

  {#if error}<p class="err">{error}</p>{/if}
  {#if seedMsg}<p class="seed-ok">{seedMsg}</p>{/if}

  <AlertBanner {alerts} />

  <QuickActions actions={quickActions} onAction={onNavigate} />

  <div class="dash-kpis">
    <KpiCard label="Ocupación" value="{semaforo?.tasaOcupacionPct ?? 0}%" hint="{semaforo?.ocupadas ?? 0} / {semaforo?.totalCamas ?? 0} camas" tone="total" />
    <KpiCard label="Facturado (cuentas)" value={fmtQ(facturadoMes)} hint="{cuentasAbiertas} abiertas" tone="brand" />
    <KpiCard label="Cirugías hoy" value={resumen?.cirugiasHoy ?? 0} hint="Block quirúrgico" tone="neutral" />
    <KpiCard label="Urgencias" value={resumen?.urgenciasActivas ?? 0} hint="ER / URG activos" tone="danger" />
  </div>

  <div class="dash-kpis compact">
    <KpiCard label="Lab pendiente" value={labPendientes} tone="neutral" />
    <KpiCard label="Imagen pend." value={imgPendientes} tone="neutral" />
    <KpiCard label="Ingresos activos" value={resumen?.ingresosActivos ?? 0} tone="success" />
    <KpiCard label="Kárdex pend." value={resumen?.kardexPendientes ?? 0} tone="warning" />
  </div>

  <div class="dash-grid">
    {#if tenantCtx.hasModule('hospitalizacion')}
      <div class="col-8">
        <BedMapWidget camas={resumen?.camas} onCamaClick={() => onNavigate('semaforo')} />
      </div>
      <div class="col-4">
        <BedSemaphoreGrid data={semaforo} compact showTables={true} />
      </div>
    {/if}

    {#if tenantCtx.hasModule('hospitalizacion')}
      <div class="col-6"><ActiveIngresosQueue limit={8} /></div>
      <div class="col-6"><AltasPendientesWidget limit={8} {onNavigate} /></div>
    {/if}

    {#if tenantCtx.hasModule('laboratorio')}<div class="col-4"><LabQueueWidget limit={6} interactive /></div>{/if}
    {#if tenantCtx.hasModule('imagenologia')}<div class="col-4"><ImagenQueueWidget limit={6} {onNavigate} /></div>{/if}
    {#if tenantCtx.hasModule('facturacion')}<div class="col-4"><OpenCuentasWidget limit={6} /></div>{/if}

    {#if tenantCtx.hasModule('farmacia-hospitalaria')}<div class="col-6"><KardexQueueWidget limit={6} /></div>{/if}
    {#if tenantCtx.hasModule('citas')}<div class="col-6"><CitasHoyQueue limit={8} interactive /></div>{/if}

    {#if tenantCtx.hasModule('quirofano')}<div class="col-6"><QuirofanoAgendaWidget limit={6} {onNavigate} /></div>{/if}
    {#if tenantCtx.hasModule('facturacion')}<div class="col-6"><OpenCuentasWidget limit={8} /></div>{/if}

    {#if tenantCtx.hasModule('hospitalizacion') || tenantCtx.hasModule('laboratorio')}
      <div class="col-12"><OpsTimeline limit={12} /></div>
    {/if}
  </div>
</DashboardLayout>

<style>
  .err { color: var(--danger); font-size: 0.88rem; }
  .seed-ok { color: var(--success); font-size: 0.85rem; margin: 0 0 0.5rem; }
  .glass-btn {
    padding: 0.45rem 0.85rem;
    border-radius: 999px;
    font: inherit;
    font-size: 0.82rem;
    cursor: pointer;
    font-weight: 600;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    border: 1px solid var(--glass-border);
    background: var(--glass-bg-card);
    color: var(--text-secondary);
    transition: all 0.25s ease;
  }
  .glass-btn:hover {
    background: var(--brand-surface);
    border-color: var(--brand-strong);
    color: var(--brand-deep);
    box-shadow: 0 4px 16px rgba(124,58,237,0.15);
  }
  .seed-btn {
    border: 0;
    background: linear-gradient(135deg, #10b981, #059669);
    color: #fff;
    font-weight: 700;
  }
  .seed-btn:hover {
    background: linear-gradient(135deg, #059669, #047857);
    color: #fff;
    box-shadow: 0 4px 16px rgba(16,185,129,0.3);
  }
</style>
